import { Skeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn, TableRowProps } from "@dash/table";
import { useTheme } from "@emotion/react";
import _ from "lodash";

import { Row, SyncOperation } from "@megaron/import-export";

type Props<T extends Row> = {
  isLoading: boolean;
  operations: SyncOperation<T>[] | undefined;
  labels: { [key in keyof T]?: string };
};

export const SyncOperationsTable = <T extends Row>(props: Props<T>) => {
  const theme = useTheme();
  if (!props.operations) return <Skeleton />;

  const keys = Array.from(
    new Set(
      props.operations.flatMap((op) =>
        _(op.properties)
          .pickBy((value, key) => value !== undefined)
          .keys()
          .value(),
      ),
    ),
  );

  const propColumns: TableHeadColumn<string>[] = keys.map((key) => ({
    label: props.labels[key] ?? key,
  }));

  const allColumns: TableHeadColumn<string>[] = [{ label: "Id" }, ...propColumns];

  const rows: TableRowProps[] = props.operations.map((op) => ({
    cellsContent: [
      {
        element: op.id,
        cellCss:
          op.action === "create"
            ? {
                background: theme.colors.primaryLight,
                color: theme.colors.primary,
                fontWeight: 700,
              }
            : undefined,
      },
      ...keys.map<RowCellContent>((key) => ({
        element: new String(op.properties[key] ?? "-"),
        cellCss:
          op.properties[key] !== undefined
            ? {
                background: theme.colors.primaryLight,
                color: theme.colors.primary,
                fontWeight: 700,
              }
            : undefined,
      })),
    ],
  }));

  return (
    <div css={{ overflow: "auto" }}>
      <Table
        columns={allColumns}
        css={{
          whiteSpace: "nowrap",
          "td:first-child": {
            padding: "0.75rem",
          },
        }}
      >
        <TableBody isLoading={props.isLoading} rows={rows} />
        {rows.length === 0 && <div css={{ padding: "1rem" }}>Bez zmian</div>}
      </Table>
    </div>
  );
};
